import { z } from 'zod';
import { MUSIC_LESSON_FORMAT_VALUES, MUSIC_PHYSICAL_VENUE_VALUES } from '@tutor/types';

const hasSelectedTime = (value: Record<string, string[]>) =>
  Object.values(value).some((slots) => slots.length > 0);

const parentSteps = [
  z.object({
    tuitionType: z.enum(['physical', 'online', 'both'], {
      message: 'Please choose how you would like the lessons to take place',
    }),
    lessonFormat: z.enum(MUSIC_LESSON_FORMAT_VALUES, {
      message: 'Please choose a lesson format',
    }),
    physicalVenue: z.enum(MUSIC_PHYSICAL_VENUE_VALUES).or(z.literal('')).default(''),
  }),
  z.object({
    instrument: z.string().min(1, 'Please choose the instrument the student wants to learn').max(100, 'Please keep the instrument name under 100 characters'),
    studentAge: z.coerce.number({ message: "Please enter the student's age" }).int("Please enter the student's age as a whole number").min(1, 'Please enter an age between 1 and 100').max(100, 'Please enter an age between 1 and 100'),
    musicLevel: z.string().min(1, "Please choose the student's current music level").max(100, 'Please keep the music level under 100 characters'),
    learningGoal: z.string().max(200, 'Please keep the learning goal under 200 characters'),
    hasOwnInstrument: z.boolean({ message: 'Please tell us whether the student has their own instrument' }),
    examBoard: z.string().max(100, 'Please keep the exam board under 100 characters'),
  }),
  z.object({
    tuitionType: z.string(),
    teachingLanguages: z.array(z.string()).min(1, 'Please choose at least one language for the lessons'),
    lessonsPerWeek: z.number({ message: 'Please choose how many lessons are needed each week' }).int('Please choose a whole number of lessons').min(1, 'Please choose at least one lesson per week').max(7, 'Please choose no more than seven lessons per week'),
    lessonDuration: z.number({ message: 'Please choose how long each lesson should be' }).min(0.5, 'Please choose a lesson duration of at least 30 minutes').max(8, 'Please choose a lesson duration of no more than 8 hours'),
    state: z.string(),
    district: z.string(),
    areaNote: z.string().max(80, 'Please keep the area details under 80 characters'),
    preferredTimes: z.record(z.string(), z.array(z.string())).refine(hasSelectedTime, 'Please choose at least one preferred lesson time'),
  }).superRefine((data, ctx) => {
    if (data.tuitionType !== 'online' && !data.state) ctx.addIssue({ code: 'custom', path: ['state'], message: 'Please choose the state where lessons will take place' });
    if (data.tuitionType !== 'online' && !data.district) ctx.addIssue({ code: 'custom', path: ['district'], message: 'Please choose the district where lessons will take place' });
  }),
  z.object({
    hourlyRate: z.number({ message: 'Please choose your maximum hourly budget' }).min(10, 'Please choose a budget of at least RM10 per hour').max(1000, 'Please choose a budget of no more than RM1,000 per hour'),
    notes: z.string().max(500, 'Please keep your remarks under 500 characters'),
    termsAccepted: z.literal(true, { message: 'Please agree to the Terms of Service and Privacy Policy to continue' }),
  }),
];

export const parseMusicParentStep = (step: number, data: unknown) => parentSteps[step].safeParse(data);

const tutorSteps = [
  z.object({
    tuitionType: z.enum(['physical', 'online', 'both'], {
      message: 'Please choose how you would like to teach',
    }),
    lessonFormats: z.array(z.enum(MUSIC_LESSON_FORMAT_VALUES)).min(1, 'Please choose at least one lesson format'),
    physicalVenues: z.array(z.enum(MUSIC_PHYSICAL_VENUE_VALUES)).default([]),
    preferredState: z.string(),
    preferredDistricts: z.array(z.string()),
  }).superRefine((data, ctx) => {
    if (data.tuitionType !== 'online' && !data.preferredState) ctx.addIssue({ code: 'custom', path: ['preferredState'], message: 'Please choose the state where you can teach' });
    if (data.tuitionType !== 'online' && data.preferredDistricts.length === 0) ctx.addIssue({ code: 'custom', path: ['preferredDistricts'], message: 'Please choose at least one district where you can teach' });
  }),
  z.object({
    instruments: z.array(z.string().min(1).max(100)).min(1, 'Please choose at least one instrument you can teach'),
    musicLevels: z.array(z.string().min(1).max(100)).min(1, 'Please choose at least one music level you can teach'),
    ageGroups: z.array(z.string().min(1).max(100)).min(1, 'Please choose at least one student age group'),
    availability: z.record(z.string(), z.array(z.string())).refine(hasSelectedTime, 'Please choose at least one time when you are available to teach'),
    examBoards: z.array(z.string().max(100, 'Please keep each exam board under 100 characters')),
  }),
  z.object({
    teachingExperienceYears: z.coerce.number({ message: 'Please choose how many years you have taught music' }).int('Please enter your teaching experience as a whole number').min(0, 'Teaching experience cannot be less than 0 years').max(80, 'Please enter no more than 80 years of teaching experience'),
    qualifications: z.array(z.object({
      type: z.string().min(1, 'Please choose a qualification type').max(100, 'Please keep the qualification type under 100 characters'),
      title: z.string().max(200, 'Please keep the qualification title under 200 characters'),
      examBoard: z.string().max(100, 'Please keep the exam board under 100 characters').optional(),
      level: z.string().max(100, 'Please keep the qualification level under 100 characters').optional(),
      year: z.number().int('Please enter the qualification year as a whole number').min(1900, 'Please enter a qualification year from 1900 onwards').max(2100, 'Please enter a qualification year no later than 2100').optional(),
      documentUrl: z.string().url('Please enter a valid document link').optional(),
    })),
    portfolioItems: z.array(z.object({
      type: z.string().min(1, 'Please choose a portfolio type').max(100, 'Please keep the portfolio type under 100 characters'),
      title: z.string().max(200, 'Please keep the portfolio title under 200 characters'),
      url: z.string().url('Please enter a valid portfolio link').or(z.literal('')).optional(),
      instrument: z.string().max(100, 'Please keep the instrument name under 100 characters').optional(),
      description: z.string().max(1000, 'Please keep the portfolio description under 1,000 characters').optional(),
    })),
  }),
  z.object({
    teachingLanguages: z.array(z.string()).min(1, 'Please choose at least one language you can teach in'),
    hourlyRate: z.number({ message: 'Please choose your hourly rate' }).min(10, 'Please choose an hourly rate of at least RM10').max(1000, 'Please choose an hourly rate of no more than RM1,000'),
    aboutMe: z.string().trim().min(20, 'Please tell students a little more about your teaching (at least 20 characters)').max(2000, 'Please keep your introduction under 2,000 characters'),
    agreedToTerms: z.literal(true, { message: 'Please agree to the Tutor Terms of Service and Privacy Policy to continue' }),
  }),
];

export const parseMusicTutorStep = (step: number, data: unknown) => tutorSteps[step].safeParse(data);
